import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { parse } from '@fint/money';
import { Card, CardContent, Money } from '@/features/common/ui';

export type Basis = 'accrual' | 'cash';

/**
 * The pieces "Money in and out" and the cross-business Overview share.
 *
 * ── WHY THESE ARE ONE FILE AND NOT TWO COPIES ──
 * The Overview is the per-org section's sibling: the same three tiles, the
 * same basis toggle, the same signed figure. If each screen carried its own
 * copy, the first divergence - one screen switching the toggle's wording, the
 * other keeping it - would leave the two disagreeing about what "When
 * invoiced" means, on two pages a person reads one after the other. One
 * component each, imported by both, is what keeps them in step by
 * construction rather than by review.
 */

/**
 * Which definition of income the whole section is on.
 *
 * One control, moving every figure together, because a screen that is half
 * accrual and half cash is a screen nobody can read. The label says what each
 * one counts rather than naming the accounting term, which is the word the
 * reader is least likely to know and the one they need least.
 */
export function BasisToggle({ value, onChange }: { value: Basis; onChange: (basis: Basis) => void }) {
  return (
    <div role="group" aria-label="Count income when" className="flex gap-1.5">
      {(
        [
          ['accrual', 'When invoiced'],
          ['cash', 'When paid'],
        ] as const
      ).map(([key, label]) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className={
            value === key
              ? 'inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary touch:min-h-11'
              : 'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted active:bg-muted touch:min-h-11'
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** What each basis counts, in the words the section's subtitle uses. */
export function describeBasis(basis: Basis): string {
  return basis === 'accrual'
    ? 'Income counts an invoice when you raise it, plus money received with no invoice behind it.'
    : 'Income counts an invoice when it is settled, plus money received with no invoice behind it.';
}

/** The smallest a figure may be scaled before it stops being a headline: 24px * 0.55 ≈ 13px. */
const FIGURE_FLOOR = 0.55;

/**
 * A figure that fits its tile, whatever the figure is.
 *
 * ── WHY THIS MEASURES RATHER THAN PICKING A SIZE ──
 * On a 390px phone a half-width tile gives its number 127 pixels. At the
 * tile's 24px, "₹18,91,045.54" wants 151 and "₹1,50,49,610.71" wants 169 -
 * so both were CUT OFF mid-figure, on the number the screen exists to show,
 * for any book turning over more than about ten lakh. Nothing said they had
 * been cut: the last digits were simply not there.
 *
 * A type step cannot fix it. The string is 13 characters at ten lakh and 17
 * at ten crore, and the one size that fits the longest is a caption at the
 * shortest. So the figure is measured against the space it actually has and
 * scaled to fit - never up, so every figure that already fits keeps the full
 * 24px, and a 5,000 rupee month looks exactly as it does today.
 *
 * ── WHY A TRANSFORM AND NOT A FONT SIZE ──
 * Changing the font size changes what the text measures, which is a loop.
 * A transform does not: the span is laid out once at 24px, and `scale` only
 * draws it smaller - sharply, since text scales as vectors. The line keeps
 * the height of the unscaled type and centres what is drawn in it, so a
 * shrunk figure does not make its tile shorter than the one beside it.
 */
function Figure({ children }: { children: React.ReactNode }) {
  const line = useRef<HTMLParagraphElement>(null);
  const text = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);

  const fit = useCallback(() => {
    const available = line.current?.clientWidth ?? 0;
    const wanted = text.current?.scrollWidth ?? 0;
    if (!available || !wanted) return;
    // `wanted` is measured at scale 1 whatever the current scale is: a
    // transform does not change layout, which is the whole reason for it.
    const next = wanted > available ? Math.max(FIGURE_FLOOR, available / wanted) : 1;
    setScale((current) => (Math.abs(current - next) > 0.005 ? next : current));
  }, []);

  /*
    Layout effect, not effect: the figure must be right in the frame it is
    first painted, not one frame later - a headline that visibly snaps
    smaller reads as a bug even when it ends up correct. No dependency list,
    because the thing to react to is the rendered width of a `ReactNode` the
    tile was handed, which nothing here can put in one; `fit` is two DOM
    reads and sets state only when the scale actually moves, so the render
    after it settles.
  */
  useLayoutEffect(fit);

  // The width can change without a render of ours: a rotation, a resized
  // window, the sidebar opening. One observer for the life of the tile.
  useLayoutEffect(() => {
    const box = line.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [fit]);

  return (
    <p ref={line} className="mt-2 flex h-8 items-center overflow-hidden text-2xl font-semibold tabular-nums">
      <span ref={text} className="whitespace-nowrap" style={{ transform: `scale(${scale})`, transformOrigin: 'left center' }}>
        {children}
      </span>
    </p>
  );
}

export function Tile({
  label,
  value,
  caption,
}: {
  label: string;
  value: React.ReactNode;
  caption?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="pt-4">
        <p className="text-sm text-muted-foreground">{label}</p>
        <Figure>{value}</Figure>
        {caption ? <p className="mt-1 text-xs text-muted-foreground">{caption}</p> : null}
      </CardContent>
    </Card>
  );
}

/**
 * A signed figure. The `+`/`−` carries the meaning; the colour reinforces it.
 *
 * `−` is U+2212 rather than a hyphen: at this size a hyphen reads as a dash and
 * all but disappears at the left edge of a right-aligned column, which is the
 * one place the sign has to be unmissable.
 */
export function Signed({ value, currency }: { value: string; currency: string }) {
  const money = parse(value, currency);
  const negative = money.minor < 0;
  return (
    <span className={negative ? 'text-destructive' : 'text-success'}>
      {negative ? '−' : '+'}
      <Money value={negative ? value.replace('-', '') : value} currency={currency} showSymbol={false} />
    </span>
  );
}
